import React, {ReactElement, useEffect, useState} from 'react';
import {
  formatResponse,
  NumericSeriesPoint,
  Series,
  SeriesResponse,
} from '../../../services/SeriesDataService';
import CRUDService from '../../../services/CRUDService';
import LineGraph, {LineGraphProps} from '../../../components/LineGraph';
import {formatLabel} from '../../college-portal/join-page-signup-chart/join-page-signup-chart';
import {pluralize, titleize, withCommas} from '../../../../shared/utils';
import styles from './styles.module.scss';
import MiniInfoCardContainer from '../../../../shared/components/college-profile/mini-info-card-container';
import MiniInfoCard from '../../../../shared/components/college-profile/mini-info-card';
import {Card, CardHeader} from '@mui/material';
import req from '../../../../shared/req';

interface AdminStats {
  totalCurrentHsStudents: number;
  totalCurrentTransferStudents: number;
  totalHsStudents: number;
  totalTransferStudents: number;
  hsSignupsByMonth: SeriesResponse;
  transferSignupsByMonth: SeriesResponse;
}

interface SignupsOverTimeMeta {
  total: number;
}

export type SignupsData = Series<NumericSeriesPoint, SignupsOverTimeMeta>;

const buildMeta =
  (seriesKey: string) =>
  (meta: SignupsOverTimeMeta, index: number, response: SeriesResponse): SignupsOverTimeMeta => {
    meta.total += response.series[seriesKey][index];
    return meta;
  };

const AdminStatsService = new CRUDService('/v1/admin');

const Loading = (): ReactElement => (
  <div className={styles.container}>
    <div className="row">
      <Card className="p-12 mb-24">
        <div>
          <div className="shared-shimmer-text full"></div>
          <div className="shared-shimmer-text large"></div>
          <div className="shared-shimmer-text large"></div>
          <div className="shared-shimmer-text medium"></div>
        </div>
      </Card>
    </div>
    <div className="row">
      <Card className="p-12">
        <div>
          <div className="shared-shimmer-text full"></div>
          <div className="shared-shimmer-text large"></div>
          <div className="shared-shimmer-text large"></div>
          <div className="shared-shimmer-text medium"></div>
        </div>
      </Card>
    </div>
  </div>
);

export const formatYearLabel: LineGraphProps['labels'] = ({datum}) => {
  const timeframe = datum.x.getFullYear();
  const count = datum.y.toLocaleString();
  const student = pluralize(datum.y, 'student');
  return `${count} ${student} in ${timeframe}`;
};

export default function Home() {
  const [name, setName] = useState<string>('Admin');
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [hsSignupsByMonth, setHsSignupsByMonth] = useState<Series<
    NumericSeriesPoint,
    SignupsOverTimeMeta
  > | null>(null);
  const [transferSignupsByMonth, setTransferSignupsByMonth] = useState<Series<
    NumericSeriesPoint,
    SignupsOverTimeMeta
  > | null>(null);

  useEffect(() => {
    AdminStatsService.custom<AdminStats>('stats').then(setStats);
    req<{email: string}>({url: '/v1/basic/me?fields=email'}).then(({email}) => {
      const userName = email.split('@')[0].split('+')[0].split('.').join(' ');
      setName(titleize(userName));
    });
  }, []);

  useEffect(() => {
    if (!stats) {
      return;
    }

    const initialData: SignupsData = {
      meta: {total: 0},
      series: {
        monthSignups: [],
      },
    };

    setHsSignupsByMonth(
      formatResponse<SignupsOverTimeMeta>(
        stats.hsSignupsByMonth,
        initialData,
        buildMeta('monthSignups')
      )
    );
  }, [stats]);

  useEffect(() => {
    if (!stats) {
      return;
    }

    const initialData: SignupsData = {
      meta: {total: 0},
      series: {
        monthSignups: [],
      },
    };

    setTransferSignupsByMonth(
      formatResponse<SignupsOverTimeMeta>(
        stats.transferSignupsByMonth,
        initialData,
        buildMeta('monthSignups')
      )
    );
  }, [stats]);

  return (
    <div className="container-fluid">
      <h2>Welcome {name}</h2>
      <div className={styles.content}>
        <div className={styles.container}>
          {!stats && <Loading />}
          {stats && (
            <MiniInfoCardContainer>
              <MiniInfoCard
                label="Current HS Students"
                value={withCommas(stats.totalCurrentHsStudents)}
              />
              <MiniInfoCard
                label="Current Transfer Students"
                value={withCommas(stats.totalCurrentTransferStudents)}
              />
              <MiniInfoCard
                label="All time HS Students"
                value={withCommas(stats.totalHsStudents)}
              />
              <MiniInfoCard
                label="All time Transfer Students"
                value={withCommas(stats.totalTransferStudents)}
              />
            </MiniInfoCardContainer>
          )}
          <div className="row">
            <div className="col-lg-6 col-sm-6 col-xs-12 mb-24">
              {hsSignupsByMonth && (
                <Card>
                  <CardHeader
                    title={<h3 className="shared-font subheading">High School Signups by month</h3>}
                  />
                  <LineGraph
                    data={[hsSignupsByMonth.series.monthSignups]}
                    labels={formatLabel}
                    legendLabels={[]}
                  />
                </Card>
              )}
            </div>
            {transferSignupsByMonth && (
              <div className="col-lg-6 col-sm-6 col-xs-12">
                <Card>
                  <CardHeader
                    title={<h3 className="shared-font subheading">Transfer Signups by month</h3>}
                  />
                  <LineGraph
                    data={[transferSignupsByMonth.series.monthSignups]}
                    labels={formatYearLabel}
                    legendLabels={[]}
                  />
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
